/**
 * Account Service
 *
 * API client for chart of accounts management.
 */

import { get, post, patch, buildQueryString, type ApiResponse, type PageInfo } from './api-client';
import {
  ACCOUNT_TYPE_LABELS,
  type AccountType,
  type NormalBalance,
} from '../constants/labels';
import { buildPaginationParams } from '../utils/api-helpers';
import { getLabel } from '../utils/labels';

import type { Money, AuditInfo } from '../types/common';

// Re-export types for backward compatibility
export type { Money, AuditInfo } from '../types/common';
export type { AccountType, NormalBalance } from '../constants/labels';

// ============================================================================
// Types
// ============================================================================

export interface Account {
  id: string;
  orgId: string;
  code: string;
  name: string;
  description: string;
  accountType: AccountType;
  accountSubtype: string;
  normalBalance: NormalBalance;
  parentId?: string;
  path: string;
  depth: number;
  isHeader: boolean;
  isSystem: boolean;
  isActive: boolean;
  isControl?: boolean;
  currency?: string;
  systemTag?: string;
  tags?: string[];
  /**
   * Populated server-side when this account backs a party's subsidiary ledger
   * entry (receivable/payable/advance/vendor advance). Use this to hide
   * per-party sub-accounts from generic account pickers.
   */
  linkedPartyId?: string;
  /** GST ledger identity — set only on GST tax books. */
  gstComponent?: string; // 'cgst' | 'sgst' | 'igst'
  gstDirection?: string; // 'output' | 'input'
  gstRate?: number;      // rate this book is scoped to, e.g. 9; unset = consolidated
  audit: AuditInfo;
}

export interface CreateAccountRequest {
  code: string;
  name: string;
  description?: string;
  accountType: AccountType;
  accountSubtype?: string;
  parentId?: string;
  isHeader?: boolean;
  isControl?: boolean;
  currency?: string;
  tags?: string[];
  idempotencyKey: string;
  /** Reserved purpose tag (e.g. 'cgst_output', 'opening_balance'). */
  systemTag?: string;
  /** GST ledger identity — set these to create a GST tax book. */
  gstComponent?: string; // 'cgst' | 'sgst' | 'igst'
  gstDirection?: string; // 'output' | 'input'
  gstRate?: number;      // e.g. 9; unset = consolidated bucket
}

export interface UpdateAccountRequest {
  name?: string;
  description?: string;
  parentId?: string;
  tags?: string[];
  code?: string;
}

export interface ListAccountsFilter {
  accountType?: AccountType;
  includeInactive?: boolean;
  includeHeaders?: boolean;
  parentId?: string;
}

export interface ListAccountsResponse {
  accounts: Account[];
  pageInfo: PageInfo;
}


export interface AccountBalance {
  accountId: string;
  asOfDate: string;
  cumulativeDebit: Money;
  cumulativeCredit: Money;
  pendingDebit: Money;
  pendingCredit: Money;
  balance: Money;
  availableBalance: Money;
}

export interface BatchAccountBalanceResult {
  balances: Record<string, AccountBalance>;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a new account.
 */
export async function createAccount(
  orgId: string,
  data: CreateAccountRequest
): Promise<ApiResponse<Account>> {
  return post<Account>(`/organizations/${orgId}/accounts`, data);
}

/**
 * Get account by ID.
 */
export async function getAccount(
  orgId: string,
  accountId: string
): Promise<ApiResponse<Account>> {
  return get<Account>(`/organizations/${orgId}/accounts/${accountId}`);
}

/**
 * Update an account.
 */
export async function updateAccount(
  orgId: string,
  accountId: string,
  data: UpdateAccountRequest
): Promise<ApiResponse<Account>> {
  return patch<Account>(`/organizations/${orgId}/accounts/${accountId}`, data);
}

/**
 * List accounts (chart of accounts).
 */
export async function listAccounts(
  orgId: string,
  filter?: ListAccountsFilter,
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<ListAccountsResponse>> {
  const query = buildQueryString({
    ...buildPaginationParams(pageSize, pageToken),
    accountType: filter?.accountType,
    includeInactive: filter?.includeInactive,
    includeHeaders: filter?.includeHeaders,
    parentId: filter?.parentId,
  });
  return get<ListAccountsResponse>(`/organizations/${orgId}/accounts${query}`);
}

/**
 * Get account balance.
 */
export async function getAccountBalance(
  orgId: string,
  accountId: string,
  asOfDate?: string
): Promise<ApiResponse<AccountBalance>> {
  const query = buildQueryString({ asOfDate });
  return get<AccountBalance>(`/organizations/${orgId}/accounts/${accountId}/balance${query}`);
}

/**
 * Get balances for many accounts in a single request.
 */
export async function getBatchAccountBalances(
  orgId: string,
  accountIds: string[],
  asOfDate?: string
): Promise<ApiResponse<BatchAccountBalanceResult>> {
  return post<BatchAccountBalanceResult>(`/organizations/${orgId}/accounts/batch-balance`, { accountIds, asOfDate: asOfDate ?? '' });
}

/**
 * Deactivate an account.
 */
export async function deactivateAccount(
  orgId: string,
  accountId: string
): Promise<ApiResponse<Account>> {
  return post<Account>(`/organizations/${orgId}/accounts/${accountId}:deactivate`);
}

/**
 * Reactivate an account.
 */
export async function reactivateAccount(
  orgId: string,
  accountId: string
): Promise<ApiResponse<Account>> {
  return post<Account>(`/organizations/${orgId}/accounts/${accountId}:reactivate`);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the display name for an account type.
 */
export function getAccountTypeLabel(type: AccountType): string {
  return getLabel(type, ACCOUNT_TYPE_LABELS);
}

/**
 * Get the normal balance for an account type.
 */
export function getNormalBalance(type: AccountType): NormalBalance {
  switch (type) {
    case 'ACCOUNT_TYPE_ASSET':
    case 'ACCOUNT_TYPE_EXPENSE':
      return 'NORMAL_BALANCE_DEBIT';
    case 'ACCOUNT_TYPE_LIABILITY':
    case 'ACCOUNT_TYPE_EQUITY':
    case 'ACCOUNT_TYPE_REVENUE':
      return 'NORMAL_BALANCE_CREDIT';
    default:
      return 'NORMAL_BALANCE_DEBIT';
  }
}

/**
 * Format account code with hierarchy indicator.
 */
export function formatAccountCode(account: Account): string {
  const indent = '  '.repeat(account.depth);
  return `${indent}${account.code}`;
}
